import { FaCheck } from 'react-icons/fa6';
import {
  ENGAGEMENT_STATUS,
  getEngagementStatusLabel,
} from '../../../../utils/engagement';
import { Job } from '../../../../utils/type';
import { formatDate } from '../../../../utils/jobDetail.helpers';

// Sequence for horizontal progress mapping
const CUSTOMER_TIMELINE_SEQUENCE: string[] = [
  ENGAGEMENT_STATUS.REQUESTED,
  ENGAGEMENT_STATUS.QUOTED,
  ENGAGEMENT_STATUS.ACCEPTED,
  ENGAGEMENT_STATUS.SCHEDULED,
  ENGAGEMENT_STATUS.INPROGRESS,
  'Awaiting',
  ENGAGEMENT_STATUS.COMPLETED,
];

/**
 * Enhanced Horizontal Timeline component for tracking job steps
 */
const JobProgressTimeline = ({
  displayStatus,
  job,
}: {
  displayStatus: string;
  job: Job;
}) => {
  const currentIndex = CUSTOMER_TIMELINE_SEQUENCE.indexOf(displayStatus);

  const getStepTimestamp = (step: string): string | undefined => {
    if (step === 'Requested') return job.createdAt;
    if (step === 'Quoted') return job.quotedAt;
    if (step === 'Accepted') return job.acceptedAt;
    if (step === 'Scheduled') return job.scheduledAt;
    if (step === 'InProgress') return job.inProgressAt;
    if (step === 'Awaiting') return job.completedAt;
    if (step === 'Completed' && job.completionConfirmedAt)
      return job.completionConfirmedAt;
    return undefined;
  };

  const totalSteps = CUSTOMER_TIMELINE_SEQUENCE.length;

  return (
    <div className="space-y-4">
      {/* Top Stepper Track - Continuous Centered Connecting Line */}
      <div className="relative">
        {/* Full Connecting Line Track Behind Circles */}
        <div
          className="absolute top-4 h-1 bg-slate-200 z-0"
          style={{
            left: `calc(100% / ${totalSteps} / 2)`,
            right: `calc(100% / ${totalSteps} / 2)`,
          }}
        >
          {/* Active Progress Line Fill */}
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{
              width: `${(Math.min(currentIndex, totalSteps - 1) / (totalSteps - 1)) * 100}%`,
            }}
          />
        </div>

        {/* Top Grid for Circles and Step Labels */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 relative z-10">
          {CUSTOMER_TIMELINE_SEQUENCE.map((step, i) => {
            const isPassed = currentIndex > i;
            const isCurrent = currentIndex === i;

            return (
              <div key={step} className="flex flex-col items-center min-w-0">
                {/* Node Circle with Solid Opaque Background */}
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 relative z-10 ${
                    isCurrent
                      ? 'bg-primary text-white ring-4 ring-primary/20 shadow-md scale-110'
                      : isPassed
                        ? 'bg-white text-primary border-2 border-primary shadow-2xs'
                        : 'bg-white text-slate-400 border-2 border-slate-300'
                  }`}
                >
                  {isPassed ? (
                    <FaCheck size={12} />
                  ) : isCurrent ? (
                    <div className="w-2 h-2 rounded-full bg-white animate-ping" />
                  ) : (
                    i + 1
                  )}
                </div>

                {/* Step Title Label */}
                <span
                  className={`text-[11px] font-bold mt-2 text-center transition-colors max-w-full truncate ${
                    isCurrent
                      ? 'text-primary'
                      : isPassed
                        ? 'text-slate-700'
                        : 'text-slate-400'
                  }`}
                >
                  {getEngagementStatusLabel(step)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Grid for Cards - Strictly Matching Top Grid Columns */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {CUSTOMER_TIMELINE_SEQUENCE.map((step, i) => {
          const isPassed = currentIndex > i;
          const isCurrent = currentIndex === i;
          const timestamp = getStepTimestamp(step);

          return (
            <div
              key={step}
              className={`p-2.5 rounded-xl border flex flex-col justify-between transition-all ${
                isCurrent
                  ? 'bg-primary/5 border-primary shadow-xs ring-1 ring-primary/20'
                  : isPassed
                    ? 'bg-emerald-50/50 border-emerald-200/60'
                    : 'bg-slate-50/50 border-slate-200/50 opacity-60'
              }`}
            >
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider ${
                      isCurrent
                        ? 'text-primary'
                        : isPassed
                          ? 'text-emerald-700'
                          : 'text-slate-400'
                    }`}
                  >
                    Step {i + 1}
                  </span>
                  {isCurrent && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  )}
                </div>
                <p className="text-xs font-bold text-slate-800 line-clamp-1">
                  {getEngagementStatusLabel(step)}
                </p>
              </div>

              <div className="mt-2 pt-1.5 border-t border-slate-200/50">
                <p className="text-[10px] font-medium text-slate-500">
                  {timestamp ? formatDate(timestamp) : 'Pending'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobProgressTimeline;
